/* Ayudate · Panel del estudiante
   Ruta protegida: sin un token de sesión válido no hay nada que mostrar
   acá, así que todo el componente gira alrededor de cargar()/validar
   contra obtenerDashboardEstudiante(), y de mandar a /login (el login
   único, ver Login.jsx) apenas el servidor dice que la sesión no sirve —
   mismo criterio que DashboardTutor.jsx, del que este panel copia la
   estructura general (topbar, próxima sesión destacada, secciones como
   .mini-card.dash-sec, modales vía Capa) simplificada a lo que necesita
   un estudiante. */

import React, { useCallback, useEffect, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { nombreCurso } from "../lib/formato";
import { leerSesionEstudiante, guardarSesionEstudiante, borrarSesionEstudiante } from "../lib/sesionEstudiante";
import { Capa } from "../componentes/Capa";
import { CampoPassword } from "../componentes/CampoPassword";
import { ProximaSesion } from "../componentes/ProximaSesion";
import { CancelarSesion } from "../componentes/CancelarSesion";
import { Accesibilidad, FiltrosColor } from "../componentes/Accesibilidad";
import { cargarA11y, guardarA11y, clasesA11y } from "../lib/a11y";

const CODIGOS_SESION_MUERTA = ["SESION_INVALIDA", "SESION_VENCIDA"];
const POR_PAGINA_HISTORIAL_ESTUDIANTE = 10;

function fechaHora(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const fecha = d.toLocaleDateString("es-CR", { day: "numeric", month: "long" });
  const hora = d.toLocaleTimeString("es-CR", { hour: "numeric", minute: "2-digit" });
  return `${fecha} · ${hora}`;
}

export function PaginaDashboardEstudiante() {
  const [sesion] = useState(() => leerSesionEstudiante());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [datos, setDatos] = useState(null);
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const [cambiandoCorreo, setCambiandoCorreo] = useState(false);
  const [paginaHistorial, setPaginaHistorial] = useState(1);
  const [cancelando, setCancelando] = useState(null);   // la reunión que se está cancelando
  const [a11y, setA11y] = useState(cargarA11y);
  const [exito, setExito] = useState("");
  const [formIncidente, setFormIncidente] = useState({ tutorNombre: "", curso: "", tipo: "ausencia", descripcion: "" });
  const [creandoIncidente, setCreandoIncidente] = useState(false);
  const [errorIncidente, setErrorIncidente] = useState("");

  const irALogin = useCallback((mensaje) => {
    borrarSesionEstudiante();
    irA_ruta("/login" + (mensaje ? `?msg=${encodeURIComponent(mensaje)}` : ""));
  }, []);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true); setError("");
    try {
      const r = await api.obtenerDashboardEstudiante(sesion.token);
      setDatos(r);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) {
        irALogin("Su sesión venció. Inicie sesión de nuevo.");
        return;
      }
      setError(err.message || "No pudimos cargar su panel. Pruebe de nuevo.");
    } finally {
      setCargando(false);
    }
  }, [sesion, irALogin]);

  useEffect(() => {
    if (!sesion) { irALogin(); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { guardarA11y(a11y); }, [a11y]);

  const salir = () => irALogin();

  const reportarIncidente = async () => {
    if (!formIncidente.descripcion.trim()) {
      setErrorIncidente("Describa brevemente qué pasó.");
      return;
    }
    setCreandoIncidente(true);
    setErrorIncidente("");
    try {
      await api.crearIncidenteEstudiante(sesion.token, {
        tutorNombre: formIncidente.tutorNombre.trim(),
        curso: formIncidente.curso.trim(),
        tipoIncidente: formIncidente.tipo,
        descripcion: formIncidente.descripcion.trim(),
      });
      setFormIncidente({ tutorNombre: "", curso: "", tipo: "ausencia", descripcion: "" });
      setExito("Incidente reportado. El equipo le va a dar seguimiento.");
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setErrorIncidente(err.message || "No pudimos guardar el incidente.");
    } finally {
      setCreandoIncidente(false);
    }
  };

  /* El modal solo arma el formulario; la sesión viva y el "se venció, vaya a
     /login" se quedan acá. Los demás errores se relanzan para que el modal
     los traduzca y los muestre sin cerrarse, que es donde se pueden leer. */
  const cancelarSesion = async (motivo) => {
    try {
      await api.cancelarSesionEstudiante(sesion.token, cancelando.token, motivo);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      throw err;
    }
  };

  const POR_PAGINA = POR_PAGINA_HISTORIAL_ESTUDIANTE;
  const historial = datos?.historialReuniones || [];
  const paginasHistorial = Math.max(1, Math.ceil(historial.length / POR_PAGINA));
  const paginaHistorialSegura = Math.min(paginaHistorial, paginasHistorial);
  const historialVisible = historial.slice(
    (paginaHistorialSegura - 1) * POR_PAGINA,
    paginaHistorialSegura * POR_PAGINA
  );

  return (
    <div className={`v2 ${clasesA11y(a11y)}`}>
      <style>{ESTILOS_MINIMOS}</style>
      <FiltrosColor />

      <header className="dash-topbar">
        <div className="dash-topbar-in">
          <span className="logo"><Marca /></span>
          <div className="dash-topbar-user">
            {datos?.nombre && <span>Hola, {datos.nombre}</span>}
            <button className="btn-ghost" onClick={() => setCambiandoPassword(true)}>Cambiar contraseña</button>
            <button className="btn-ghost" onClick={() => setCambiandoCorreo(true)}>Cambiar correo</button>
            <button className="btn-ghost" style={{ color: "var(--accent-hi)", borderColor: "var(--accent-hi)" }}
              onClick={() => irA_ruta("/")}>Volver a inicio</button>
            <button className="btn" onClick={salir}>Cerrar sesión</button>
          </div>
        </div>
      </header>

      <div className="mini-wrap dash-wrap">
        {cargando ? (
          <div className="mini-card"><p style={{ margin: 0 }}>Cargando su panel…</p></div>
        ) : !datos ? (
          <div className="mini-card">
            <h1>No pudimos cargar su panel</h1>
            {error && <div className="error" role="alert">{error}</div>}
            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }} onClick={cargar}>
              Reintentar
            </button>
          </div>
        ) : (
          <>
            {error && <div className="error" role="alert" style={{ marginBottom: 20 }}>{error}</div>}
            {exito && <div className="exito" role="status" style={{ marginBottom: 20 }}>{exito}</div>}

            {!!datos.proximasReuniones.length && (
              <ProximaSesion
                sesion={{ ...datos.proximasReuniones[0], estudiante: datos.proximasReuniones[0].tutorNombre }}
                nombreCurso={nombreCurso(datos.proximasReuniones[0].curso)}
                textoFecha={fechaHora(datos.proximasReuniones[0].inicio)}
              />
            )}

            <div className="mini-card dash-sec">
              <h2>Próximas reuniones</h2>
              {!datos.proximasReuniones.length ? (
                <p className="vacio">No tiene reuniones pagadas por venir.</p>
              ) : datos.proximasReuniones.map((r) => {
                const enCurso = r.fin && new Date() >= new Date(r.inicio) && new Date() < new Date(r.fin);
                return (
                  <div className="fila" key={r.token}>
                    <div className="fila-info">
                      <b>{nombreCurso(r.curso)}</b>{" "}
                      <span className={`chip ${enCurso ? "success" : "warn"}`}>
                        {enCurso ? "En curso" : "Próxima"}
                      </span>
                      <p>{r.tutorNombre} · {fechaHora(r.inicio)}</p>
                    </div>
                    <div className="fila-acciones">
                      {r.sala && (
                        <a className="btn btn-p" href={r.sala} target="_blank" rel="noopener noreferrer">
                          Unirse a la reunión
                        </a>
                      )}
                      <button className="btn btn-danger" onClick={() => setCancelando(r)}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mini-card dash-sec">
              <h2>Incidentes y reportes</h2>
              <p className="ayuda" style={{ margin: "0 0 6px" }}>
                Registro rápido de una ausencia del tutor, una falla técnica u otro problema
                puntual de una sesión, para que el equipo le dé seguimiento. Su nombre ya va
                incluido, no hace falta que lo escriba.
              </p>
              {errorIncidente && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorIncidente}</div>}

              <div style={{ display: "flex", gap: "12px 16px", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-tutor" style={{ marginTop: 0 }}>Tutor (opcional)</label>
                  <input id="inc-tutor" className="in" value={formIncidente.tutorNombre}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, tutorNombre: e.target.value }))} />
                </div>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-curso" style={{ marginTop: 0 }}>Curso (opcional)</label>
                  <input id="inc-curso" className="in" value={formIncidente.curso}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, curso: e.target.value }))} />
                </div>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-tipo" style={{ marginTop: 0 }}>Tipo</label>
                  <select id="inc-tipo" className="in" value={formIncidente.tipo}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, tipo: e.target.value }))}>
                    <option value="ausencia">Ausencia del tutor</option>
                    <option value="tecnico">Problema técnico</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              <label className="lb" htmlFor="inc-descripcion">Qué pasó</label>
              <textarea id="inc-descripcion" className="in ta" rows={3} maxLength={500}
                value={formIncidente.descripcion}
                placeholder="Describa brevemente el incidente, con la fecha de la sesión si aplica."
                onChange={(e) => setFormIncidente((f) => ({ ...f, descripcion: e.target.value }))} />
              <button className="btn btn-p" style={{ marginTop: 14 }}
                disabled={creandoIncidente} onClick={reportarIncidente}>
                {creandoIncidente ? "Guardando…" : "Reportar incidente"}
              </button>
            </div>

            <div className="mini-card dash-sec">
              <h2>Sesiones completadas</h2>
              {!historial.length ? (
                <p className="vacio">Todavía no tiene sesiones completadas.</p>
              ) : (
                <>
                  {historialVisible.map((r) => (
                    <div className="fila" key={r.token}>
                      <div className="fila-info">
                        <b>{nombreCurso(r.curso)}</b>
                        <p>
                          {r.tutorNombre} · {fechaHora(r.inicio)}
                          {r.calificacion ? ` · ${r.calificacion}★` : ""}
                        </p>
                      </div>
                      <span className="chip off">Completada</span>
                    </div>
                  ))}
                  {paginasHistorial > 1 && (
                    <nav className="pag" aria-label="Páginas de sesiones completadas">
                      <button type="button" className="pbtn" disabled={paginaHistorialSegura === 1}
                        onClick={() => setPaginaHistorial(paginaHistorialSegura - 1)}
                        aria-label="Página anterior">‹</button>
                      {Array.from({ length: paginasHistorial }, (_, i) => i + 1).map((p) => (
                        <button type="button" key={p}
                          className={`pbtn ${p === paginaHistorialSegura ? "on" : ""}`}
                          aria-current={p === paginaHistorialSegura ? "page" : undefined}
                          aria-label={`Página ${p}`}
                          onClick={() => setPaginaHistorial(p)}>{p}</button>
                      ))}
                      <button type="button" className="pbtn" disabled={paginaHistorialSegura === paginasHistorial}
                        onClick={() => setPaginaHistorial(paginaHistorialSegura + 1)}
                        aria-label="Página siguiente">›</button>
                    </nav>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {cancelando && (
        <CancelarSesion
          titulo="Cancelar esta tutoría"
          resumen={`${nombreCurso(cancelando.curso)} con ${cancelando.tutorNombre}, ${fechaHora(cancelando.inicio)}`}
          aviso={"Cancelando con más de 24 horas de anticipación se le devuelve el 80% de lo que "
            + "pagó; con 24 horas o menos, el 60%. Le avisamos al tutor y al equipo, que le hace "
            + "la devolución por SINPE."}
          onCancelar={cancelarSesion}
          onCerrar={() => setCancelando(null)}
          onListo={async () => {
            setCancelando(null); await cargar();
            setExito("Tutoría cancelada. Le avisamos al tutor y al equipo.");
          }}
        />
      )}

      {cambiandoPassword && (
        <CambiarPassword
          sessionToken={sesion.token}
          onCerrado={() => setCambiandoPassword(false)}
          onGuardado={() => {
            setCambiandoPassword(false);
            setExito("Contraseña actualizada.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {cambiandoCorreo && (
        <CambiarCorreo
          sessionToken={sesion.token}
          correoActual={datos?.correo}
          onCerrado={() => setCambiandoCorreo(false)}
          onGuardado={(nuevoCorreo) => {
            setCambiandoCorreo(false);
            guardarSesionEstudiante(sesion.token, nuevoCorreo);
            setDatos((d) => (d ? { ...d, correo: nuevoCorreo } : d));
            setExito("Correo actualizado.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      <Accesibilidad ajustes={a11y} setAjustes={setA11y} />
    </div>
  );
}

function CambiarPassword({ sessionToken, onCerrado, onGuardado, onSesionMuerta }) {
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const validar = () => {
    if (!actual) return "Ingrese su contraseña actual.";
    if (nueva.length < 8) return "La contraseña nueva debe tener al menos 8 caracteres.";
    if (nueva !== confirmar) return "Las contraseñas nuevas no coinciden.";
    return "";
  };

  const guardar = async () => {
    const mensaje = validar();
    if (mensaje) { setError(mensaje); return; }
    setGuardando(true); setError("");
    try {
      await api.cambiarPasswordEstudiante(sessionToken, actual, nueva);
      onGuardado();
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      if (codigo === "PASSWORD_ACTUAL_INCORRECTA") setError("La contraseña actual no es correcta.");
      else setError(err.message || "No pudimos cambiar su contraseña.");
    } finally {
      setGuardando(false);
    }
  };

  const hayCambios = !!(actual || nueva || confirmar);

  return (
    <Capa onCerrar={onCerrado} etiqueta="Cambiar contraseña" confirmarSalida={hayCambios}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Cambiar contraseña</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          <label className="lb" htmlFor="pw-actual">Contraseña actual</label>
          <CampoPassword id="pw-actual" className="in" value={actual}
            autoComplete="current-password" onChange={(e) => setActual(e.target.value)} />

          <label className="lb" htmlFor="pw-nueva">Contraseña nueva</label>
          <CampoPassword id="pw-nueva" className="in" value={nueva}
            autoComplete="new-password" onChange={(e) => setNueva(e.target.value)} />

          <label className="lb" htmlFor="pw-confirmar">Confirmar contraseña nueva</label>
          <CampoPassword id="pw-confirmar" className="in" value={confirmar}
            autoComplete="new-password" onChange={(e) => setConfirmar(e.target.value)} />

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}

/* Mismo patrón que CambiarPassword: exige la contraseña actual, no alcanza
   con la sesión viva. El correo nuevo también migra en el servidor todas
   las filas de "Solicitudes" que tenía el correo anterior (ver
   cambiarCorreoEstudiante en apps-script.gs), así que el historial de
   reuniones sigue apareciendo después del cambio. */
function CambiarCorreo({ sessionToken, correoActual, onCerrado, onGuardado, onSesionMuerta }) {
  const [password, setPassword] = useState("");
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const validar = () => {
    if (!password) return "Ingrese su contraseña actual.";
    if (!nuevoCorreo.trim().includes("@")) return "Ingrese un correo válido.";
    return "";
  };

  const guardar = async () => {
    const mensaje = validar();
    if (mensaje) { setError(mensaje); return; }
    setGuardando(true); setError("");
    try {
      const r = await api.cambiarCorreoEstudiante(sessionToken, password, nuevoCorreo.trim());
      onGuardado(r.correo);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      if (codigo === "PASSWORD_ACTUAL_INCORRECTA") setError("La contraseña actual no es correcta.");
      else if (codigo === "CORREO_OCUPADO") setError("Ya existe una cuenta con ese correo.");
      else if (codigo === "CORREO_INVALIDO") setError("Ingrese un correo válido.");
      else if (codigo === "CORREO_IGUAL") setError("Ese ya es su correo actual.");
      else setError(err.message || "No pudimos cambiar su correo.");
    } finally {
      setGuardando(false);
    }
  };

  const hayCambios = !!(password || nuevoCorreo);

  return (
    <Capa onCerrar={onCerrado} etiqueta="Cambiar correo" confirmarSalida={hayCambios}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Cambiar correo</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          {correoActual && (
            <p className="ayuda" style={{ marginTop: 0 }}>Correo actual: <b>{correoActual}</b></p>
          )}

          <label className="lb" htmlFor="correo-nuevo">Correo nuevo</label>
          <input id="correo-nuevo" type="email" className="in" value={nuevoCorreo}
            autoComplete="email" onChange={(e) => setNuevoCorreo(e.target.value)} />

          <label className="lb" htmlFor="correo-password">Contraseña actual</label>
          <CampoPassword id="correo-password" className="in" value={password}
            autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}
